import { useEffect, useState, useCallback } from "react";
import { devError } from "@/lib/log";
import { api, API, fmt, formatApiError, getSessionToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";
import {
  DownloadSimple,
  UploadSimple,
  Printer,
  ArrowClockwise,
  CalendarBlank,
  Buildings,
  User as UserIcon,
  ShieldCheck,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { DefBox, Stat, todayISO } from "@/components/reports/bits";

export default function Reportes() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [sucursales, setSucursales] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [fecha, setFecha] = useState(todayISO());
  const [filtroSucursal, setFiltroSucursal] = useState("");
  const [filtroUsuario, setFiltroUsuario] = useState("");
  const [period, setPeriod] = useState("month");
  const [groupBy, setGroupBy] = useState("");
  const [daily, setDaily] = useState(null);
  const [agg, setAgg] = useState(null);
  const [conv, setConv] = useState(null);
  const [spamDefense, setSpamDefense] = useState(null);
  const [convRange, setConvRange] = useState(30); // days
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ fecha });
      if (filtroSucursal) params.set("sucursal_id", filtroSucursal);
      if (filtroUsuario) params.set("usuario_id", filtroUsuario);
      const aggParams = new URLSearchParams({ period });
      if (groupBy) aggParams.set("group_by", groupBy);
      if (filtroSucursal) aggParams.set("sucursal_id", filtroSucursal);
      if (filtroUsuario) aggParams.set("usuario_id", filtroUsuario);
      const convParams = new URLSearchParams();
      const dt = new Date();
      const df = new Date(dt);
      df.setDate(dt.getDate() - convRange);
      convParams.set("date_from", df.toISOString().slice(0, 10));
      convParams.set("date_to", dt.toISOString().slice(0, 10));
      if (filtroSucursal) convParams.set("sucursal_id", filtroSucursal);
      const [d, a, c, sd] = await Promise.all([
        api.get(`/reports/daily-cut?${params}`),
        api.get(`/reports/aggregated?${aggParams}`),
        api.get(`/reports/conversion?${convParams}`),
        api.get(`/reports/spam-defense?days=${convRange}`),
      ]);
      setDaily(d.data);
      setAgg(a.data);
      setConv(c.data);
      setSpamDefense(sd.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [fecha, filtroSucursal, filtroUsuario, period, groupBy, convRange]);

  useEffect(() => {
    (async () => {
      try {
        const [s, u] = await Promise.all([
          api.get("/sucursales"),
          isAdmin ? api.get("/users") : Promise.resolve({ data: [] }),
        ]);
        setSucursales(s.data || []);
        setUsuarios(u.data || []);
      } catch (e) {
        devError("load sucursales/users error", e);
      }
    })();
  }, [isAdmin]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const downloadFile = async (path, defaultName) => {
    try {
      const headers = {};
      const t = getSessionToken();
      if (t) headers.Authorization = `Bearer ${t}`;
      const res = await fetch(`${API}${path}`, { credentials: "include", headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = defaultName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Descarga iniciada");
    } catch (e) {
      devError("download error", e);
      toast.error(`Error al descargar: ${e.message}`);
    }
  };

  const onImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const headers = {};
      const t = getSessionToken();
      if (t) headers.Authorization = `Bearer ${t}`;
      const res = await fetch(`${API}/import/xlsx`, {
        method: "POST",
        credentials: "include",
        headers,
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Error importando");
      const total = Object.values(data.summary || {}).reduce((s, v) => s + (v.upserted || 0), 0);
      toast.success(`Importadas ${total} filas en ${Object.keys(data.summary || {}).length} colecciones`);
      loadAll();
    } catch (err) {
      devError("import error", err);
      toast.error(err.message || "Error importando");
    } finally {
      setImporting(false);
      e.target.value = "";
    }
  };

  const printReport = () => {
    document.body.classList.add("printing-report");
    window.print();
    setTimeout(() => document.body.classList.remove("printing-report"), 500);
  };

  const t = daily?.totales || {};
  const act = daily?.actividad || {};

  return (
    <div className="px-4 sm:px-8 lg:px-16 py-8 max-w-[1400px] mx-auto">
      <header className="mb-8 flex flex-wrap items-end justify-between gap-4 no-print">
        <div>
          <div className="overline text-klein">Reportes</div>
          <h1 className="h-display text-4xl sm:text-5xl font-black tracking-tighter">
            Corte del día & analítica
          </h1>
          <p className="text-sm text-muted2 mt-2 max-w-xl">
            Resumen de actividad por fecha, sucursal o usuario. Exporta toda la base de datos a Excel/CSV o impórtala desde una hoja.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={loadAll}
            data-testid="reports-refresh-btn"
            data-help="Vuelve a consultar el corte y las gráficas con los filtros actuales."
            className="border border-zinc-300 px-4 py-2 text-sm flex items-center gap-2 hover:bg-zinc-50"
          >
            <ArrowClockwise size={16} /> Refrescar
          </button>
          <button
            onClick={printReport}
            data-testid="reports-print-btn"
            data-help="Abre el diálogo del navegador para imprimir o guardar el corte como PDF."
            className="border border-zinc-300 px-4 py-2 text-sm flex items-center gap-2 hover:bg-zinc-50"
          >
            <Printer size={16} /> Imprimir
          </button>
        </div>
      </header>

      {isAdmin && (
        <section className="border border-zinc-200 p-6 mb-8 no-print" data-testid="reports-export-section">
          <div className="flex flex-wrap items-center gap-3 justify-between">
            <div>
              <div className="overline text-klein">Base de datos</div>
              <div className="text-base font-medium">Exportar / Importar todo</div>
              <div className="text-xs text-muted2 mt-1">
                Productos, ventas, suscriptores, referidos, publicaciones, sucursales y usuarios.
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => downloadFile("/export/xlsx", `vendeia-${todayISO()}.xlsx`)}
                data-testid="export-xlsx-btn"
                data-help="Descarga TODA la base de datos en un Excel con una hoja por entidad: productos, ventas, suscriptores, etc."
                className="bg-klein text-white px-4 py-2 text-sm flex items-center gap-2 hover:bg-klein/90"
              >
                <DownloadSimple size={16} /> Excel (.xlsx)
              </button>
              <button
                onClick={() => downloadFile("/export/csv", `vendeia-${todayISO()}.zip`)}
                data-testid="export-csv-btn"
                data-help="Misma información que Excel pero como archivos CSV separados dentro de un ZIP. Útil para Google Sheets o sistemas externos."
                className="border border-zinc-300 px-4 py-2 text-sm flex items-center gap-2 hover:bg-zinc-50"
              >
                <DownloadSimple size={16} /> CSV (.zip)
              </button>
              <label
                className="border border-zinc-300 px-4 py-2 text-sm flex items-center gap-2 hover:bg-zinc-50 cursor-pointer"
                data-testid="import-xlsx-label"
                data-help="Sube un Excel .xlsx (mismo formato del exportado) y los registros se actualizarán o crearán por id. Las celdas vacías NO sobreescriben datos existentes."
              >
                <UploadSimple size={16} /> {importing ? "Importando…" : "Importar Excel"}
                <input
                  type="file"
                  accept=".xlsx"
                  className="hidden"
                  onChange={onImport}
                  disabled={importing}
                  data-testid="import-xlsx-input"
                />
              </label>
            </div>
          </div>
        </section>
      )}

      <section className="border border-zinc-200 p-6 mb-8 no-print">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className="overline block mb-1">Fecha</label>
            <div className="flex items-center border border-zinc-300 px-3">
              <CalendarBlank size={14} className="text-muted2" />
              <input
                type="date"
                value={fecha}
                onChange={(e) => setFecha(e.target.value)}
                data-testid="reports-fecha-input"
                className="flex-1 px-2 py-2 text-sm outline-none"
              />
            </div>
          </div>
          <div>
            <label className="overline block mb-1">Sucursal</label>
            <div className="flex items-center border border-zinc-300 px-3">
              <Buildings size={14} className="text-muted2" />
              <select
                value={filtroSucursal}
                onChange={(e) => setFiltroSucursal(e.target.value)}
                data-testid="reports-sucursal-select"
                disabled={!isAdmin}
                data-help={isAdmin ? "Limita el reporte a una sucursal. Deja 'Todas' para ver el negocio completo." : "Como cajero solo puedes ver tu propia sucursal."}
                className="flex-1 px-2 py-2 text-sm outline-none bg-white disabled:bg-zinc-50 disabled:text-muted2"
              >
                <option value="">{isAdmin ? "Todas" : "Mi sucursal"}</option>
                {sucursales.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.nombre}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="overline block mb-1">Usuario</label>
            <div className="flex items-center border border-zinc-300 px-3">
              <UserIcon size={14} className="text-muted2" />
              <select
                value={filtroUsuario}
                onChange={(e) => setFiltroUsuario(e.target.value)}
                data-testid="reports-usuario-select"
                disabled={!isAdmin}
                data-help={isAdmin ? "Filtra el reporte por un cajero o empleado en particular." : "Como cajero solo puedes ver tus propias ventas."}
                className="flex-1 px-2 py-2 text-sm outline-none bg-white disabled:bg-zinc-50 disabled:text-muted2"
              >
                <option value="">{isAdmin ? "Todos" : "Solo yo"}</option>
                {usuarios.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name || u.email}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="overline block mb-1">Periodo</label>
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              data-testid="reports-period-select"
              className="w-full border border-zinc-300 px-3 py-2 text-sm bg-white"
            >
              <option value="day">Diario (últimos días)</option>
              <option value="week">Semanal</option>
              <option value="month">Mensual</option>
              <option value="year">Anual</option>
            </select>
          </div>
          <div>
            <label className="overline block mb-1">Agrupar por</label>
            <select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
              data-testid="reports-groupby-select"
              disabled={!isAdmin}
              data-help={isAdmin ? "Compara desempeño entre sucursales o entre vendedores en una segunda gráfica de barras." : "Solo administradores pueden agrupar."}
              className="w-full border border-zinc-300 px-3 py-2 text-sm bg-white disabled:bg-zinc-50 disabled:text-muted2"
            >
              <option value="">— sin agrupar —</option>
              {isAdmin && (
                <>
                  <option value="sucursal">Por sucursal</option>
                  <option value="usuario">Por usuario</option>
                </>
              )}
            </select>
          </div>
        </div>
      </section>

      <section className="mb-10" data-testid="daily-cut-section">
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="text-lg font-semibold">
            Corte del día — {daily?.fecha || fecha}
          </h2>
          {loading && <span className="text-xs text-muted2">cargando…</span>}
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat label="Ingresos" value={fmt(t.total_ingresos || 0)} testid="kpi-ingresos" />
          <Stat label="Utilidad" value={fmt(t.total_utilidad || 0)} testid="kpi-utilidad" tone="green" />
          <Stat label="Ticket promedio" value={fmt(t.ticket_promedio || 0)} testid="kpi-ticket" />
          <Stat label="Unidades" value={String(t.unidades_vendidas || 0)} testid="kpi-unidades" />
          <Stat label="Pedidos pagados" value={String(t.pedidos_pagados || 0)} testid="kpi-pagados" />
          <Stat label="Pendientes" value={String(t.pedidos_pendientes || 0)} testid="kpi-pendientes" />
          <Stat label="Suscriptores nuevos" value={String(act.nuevos_suscriptores || 0)} testid="kpi-subs" />
          <Stat label="Publicaciones" value={String(act.publicaciones_creadas || 0)} testid="kpi-posts" />
        </div>

        <div className="mt-6 border border-zinc-200">
          <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
            <div className="text-sm font-medium">Detalle de ventas del día</div>
            <div className="text-xs text-muted2">{daily?.detalle_ventas?.length || 0} pedidos</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50">
                <tr className="text-left">
                  <th className="px-4 py-2 font-medium">Hora</th>
                  <th className="px-4 py-2 font-medium">Producto</th>
                  <th className="px-4 py-2 font-medium">Comprador</th>
                  <th className="px-4 py-2 font-medium">Cant.</th>
                  <th className="px-4 py-2 font-medium">Total</th>
                  <th className="px-4 py-2 font-medium">Estado</th>
                  <th className="px-4 py-2 font-medium">Canal</th>
                </tr>
              </thead>
              <tbody>
                {(daily?.detalle_ventas || []).length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-6 text-center text-muted2">
                      Sin ventas registradas en esta fecha.
                    </td>
                  </tr>
                )}
                {(daily?.detalle_ventas || []).map((v) => (
                  <tr key={v.id} className="border-t border-zinc-100">
                    <td className="px-4 py-2 font-mono text-xs">{v.hora}</td>
                    <td className="px-4 py-2">{v.producto}</td>
                    <td className="px-4 py-2">{v.comprador}</td>
                    <td className="px-4 py-2">{v.cantidad}</td>
                    <td className="px-4 py-2">{fmt(v.total)}</td>
                    <td className="px-4 py-2">
                      <span className="text-xs uppercase tracking-wide">{v.estado}</span>
                    </td>
                    <td className="px-4 py-2 text-xs">{v.canal}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section data-testid="conversion-section" className="mb-10">
        <div className="flex items-baseline justify-between flex-wrap gap-3 mb-4">
          <div>
            <h2 className="text-lg font-semibold">Conversión por canal</h2>
            <div className="text-xs text-muted2">
              {conv?.period?.from} → {conv?.period?.to} · cuántos compradores iniciaron por cada canal y cuántos cerraron.
            </div>
          </div>
          <select
            value={convRange}
            onChange={(e) => setConvRange(Number(e.target.value))}
            className="border border-zinc-300 px-3 py-2 text-sm bg-white no-print"
            data-testid="conv-range-select"
          >
            <option value={7}>Últimos 7 días</option>
            <option value={30}>Últimos 30 días</option>
            <option value={90}>Últimos 90 días</option>
            <option value={365}>Último año</option>
          </select>
        </div>

        <div className="border border-zinc-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-[11px] uppercase tracking-[0.15em] text-muted2">
              <tr className="text-left">
                <th className="px-4 py-2.5 font-medium">Canal</th>
                <th className="px-4 py-2.5 font-medium text-right">Intentos</th>
                <th className="px-4 py-2.5 font-medium text-right">Conversiones</th>
                <th className="px-4 py-2.5 font-medium text-right">% conv.</th>
                <th className="px-4 py-2.5 font-medium text-right">Ingresos</th>
                <th className="px-4 py-2.5 font-medium text-right">Ticket prom.</th>
              </tr>
            </thead>
            <tbody>
              {(conv?.channels || []).map((c) => {
                const dot = {
                  stripe: "bg-klein",
                  whatsapp: "bg-[#25D366]",
                  bank: "bg-[#0D47A1]",
                  apartar: "bg-zinc-400",
                  chat_ai: "bg-[#7C5807]",
                }[c.key] || "bg-zinc-400";
                const tone =
                  c.conversion_rate >= 30
                    ? "text-[#1B5E20]"
                    : c.conversion_rate >= 10
                      ? "text-[#7C5807]"
                      : "text-muted2";
                return (
                  <tr
                    key={c.key}
                    className="border-t border-zinc-100"
                    data-testid={`conv-row-${c.key}`}
                  >
                    <td className="px-4 py-3 flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full inline-block ${dot}`} />
                      <span className="font-medium">{c.label}</span>
                    </td>
                    <td className="px-4 py-3 text-right font-mono">{c.intents}</td>
                    <td className="px-4 py-3 text-right font-mono">{c.conversions}</td>
                    <td className={`px-4 py-3 text-right font-mono font-semibold ${tone}`}>
                      {c.conversion_rate}%
                    </td>
                    <td className="px-4 py-3 text-right font-mono">{fmt(c.revenue)}</td>
                    <td className="px-4 py-3 text-right font-mono">
                      {c.avg_ticket ? fmt(c.avg_ticket) : "—"}
                    </td>
                  </tr>
                );
              })}
              {(!conv || (conv.channels || []).length === 0) && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-muted2">
                    Sin datos en el rango seleccionado.
                  </td>
                </tr>
              )}
            </tbody>
            {conv?.totals && (
              <tfoot className="bg-zinc-50 border-t border-zinc-200">
                <tr className="font-semibold">
                  <td className="px-4 py-3">Total</td>
                  <td className="px-4 py-3 text-right font-mono">{conv.totals.intents}</td>
                  <td className="px-4 py-3 text-right font-mono">{conv.totals.conversions}</td>
                  <td className="px-4 py-3 text-right font-mono">{conv.totals.conversion_rate}%</td>
                  <td className="px-4 py-3 text-right font-mono">{fmt(conv.totals.revenue)}</td>
                  <td className="px-4 py-3" />
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {conv?.channels && conv.channels.length > 0 && (
          <div
            className="mt-6 border border-zinc-200 p-4"
            style={{ height: 280, minHeight: 280 }}
            data-testid="conv-chart"
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={conv.channels}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="intents" fill="#A1A1AA" name="Intentos" />
                <Bar dataKey="conversions" fill="#00C853" name="Conversiones" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        <div className="text-[11px] text-muted2 mt-2">
          <strong>Intentos</strong>: cada vez que un comprador inició el flujo en ese canal (click WhatsApp, abrió modal de transferencia, inició checkout de tarjeta, o apartó).{" "}
          <strong>Conversiones</strong>: ventas confirmadas (pagado o enviado) en ese canal.
        </div>
      </section>

      <section data-testid="spam-defense-section" className="mb-10">
        <div className="flex items-baseline justify-between flex-wrap gap-3 mb-4">
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <ShieldCheck size={18} weight="duotone" className="text-[#1B5E20]" />
              Defensa anti-spam · ROI diario
            </h2>
            <div className="text-xs text-muted2">
              {spamDefense?.period?.from} → {spamDefense?.period?.to} · chats legítimos vs. bloqueados por la IA anti-spam (Jaccard + blocklist).
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
          <DefBox label="Legítimos" value={spamDefense?.totals?.legitimate ?? 0} color="#1B5E20" />
          <DefBox label="Bloqueados" value={spamDefense?.totals?.blocked ?? 0} color="#B71C1C" />
          <DefBox
            label="Tasa defensa"
            value={`${spamDefense?.totals?.defense_rate_pct ?? 0}%`}
            color="#1E4FCC"
          />
          <DefBox
            label="Tokens ahorrados"
            value={(spamDefense?.totals?.tokens_saved ?? 0).toLocaleString("es-MX")}
            color="#5D4037"
            sub="≈500 tok/bloqueo"
          />
          <DefBox
            label="Patrones · IPs"
            value={`${spamDefense?.patterns_learned ?? 0} · ${spamDefense?.blocked_ips ?? 0}`}
            color="#7C5807"
            sub="aprendidos · bloqueadas"
          />
        </div>

        {spamDefense?.series && spamDefense.series.length > 0 ? (
          <div
            className="border border-zinc-200 p-4"
            style={{ height: 280, minHeight: 280 }}
            data-testid="spam-defense-chart"
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={spamDefense.series}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 10 }}
                  tickFormatter={(d) => (d || "").slice(5)}
                />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar dataKey="legitimate" stackId="a" fill="#1B5E20" name="Legítimos" />
                <Bar dataKey="blocked" stackId="a" fill="#B71C1C" name="Bloqueados" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="border border-dashed border-zinc-300 p-8 text-center text-sm text-muted2">
            Sin datos en el rango seleccionado.
          </div>
        )}

        <div className="text-[11px] text-muted2 mt-2">
          <strong>Tokens ahorrados</strong>: estimación basada en ~500 tokens por respuesta de IA evitada (prompt + completion). Cada bloqueo además ahorra una alerta de Telegram y un follow-up programado.
        </div>
      </section>

      <section data-testid="aggregated-section" className="mb-10">
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="text-lg font-semibold">
            Tendencia {period === "day" ? "diaria" : period === "week" ? "semanal" : period === "year" ? "anual" : "mensual"}
            {groupBy ? ` · agrupada por ${groupBy}` : ""}
          </h2>
          <div className="text-xs text-muted2">
            Total: <strong>{fmt(agg?.totales?.ingresos || 0)}</strong> · Utilidad: {fmt(agg?.totales?.utilidad || 0)} · Margen {agg?.totales?.margen_pct || 0}%
          </div>
        </div>

        <div className="border border-zinc-200 p-4" style={{ height: 320, minHeight: 320 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={agg?.serie || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="periodo" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="ingresos" stroke="#002FA7" strokeWidth={2} name="Ingresos" />
              <Line type="monotone" dataKey="utilidad" stroke="#00C853" strokeWidth={2} name="Utilidad" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {groupBy && (agg?.grupos || []).length > 0 && (
          <div className="mt-6 border border-zinc-200 p-4" style={{ height: 320, minHeight: 320 }} data-testid="agg-group-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={agg.grupos}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="nombre" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="ingresos" fill="#002FA7" name="Ingresos" />
                <Bar dataKey="utilidad" fill="#00C853" name="Utilidad" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </section>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          aside { display: none !important; }
          main { padding: 0 !important; }
        }
      `}</style>
    </div>
  );
}
